import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Mail, ArrowRight, AlertCircle, Lock } from 'lucide-react'
import { config } from '../config'
import { useAuth } from '../hooks/useAuth'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [tempPassword, setTempPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showTempPassword, setShowTempPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  
  // Check if this is temp password flow (from email link or must_change_password flag)
  const isTempPasswordFlow = searchParams.get('temp_password') === 'true' || searchParams.get('token')
  const oneTimeToken = searchParams.get('token')

  useEffect(() => {
    // Prepopulate email if user is already logged in (from temp password login)
    if (isTempPasswordFlow && user?.email && !email) {
      setEmail(user.email)
    }
  }, [isTempPasswordFlow, user, email])

  const validateEmail = () => {
    const e: Record<string, string> = {}
    if (!email) e.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Please include an "@" in the email address'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const validatePassword = (password: string): string | null => {
    if (!password) return 'Password is required'
    
    // Check all requirements and return a single comprehensive error message
    const requirements: string[] = []
    if (password.length < 8) requirements.push('at least 8 characters')
    if (!/[A-Z]/.test(password)) requirements.push('1 uppercase letter')
    if (!/[a-z]/.test(password)) requirements.push('1 lowercase letter')
    if (!/[0-9]/.test(password)) requirements.push('1 number')
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) requirements.push('1 special character')
    
    if (requirements.length > 0) {
      return `Password must contain ${requirements.join(', ')}`
    }
    
    return null
  }

  const validatePasswordChange = () => {
    const e: Record<string, string> = {}
    
    if (!tempPassword) {
      e.tempPassword = 'Temporary password is required'
    }
    
    const newPasswordError = validatePassword(newPassword)
    if (newPasswordError) {
      e.newPassword = newPasswordError
    }
    
    if (!confirmPassword) {
      e.confirmPassword = 'Please confirm your new password'
    } else if (newPassword !== confirmPassword) {
      e.confirmPassword = 'Passwords do not match'
    }
    
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmitEmail = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validateEmail()) return
    setIsLoading(true)
    setErrors({})

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || 'Failed to send reset email')
      }

      // Always show success page (for privacy - don't reveal if email exists)
      setIsSuccess(true)
      setTimeout(() => {
        navigate('/forgot-password/sent', { state: { email } })
      }, 1500)
    } catch (err: any) {
      const message = (err as Error)?.message || ''
      setErrors({ general: message || 'Failed to send reset email. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitPasswordChange = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validatePasswordChange()) return
    setIsLoading(true)
    setErrors({})

    try {
      // If we have a one-time token, use login-with-token endpoint
      if (oneTimeToken) {
        const loginResponse = await fetch(`${config.apiBaseUrl}/api/auth/login-with-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token: oneTimeToken }),
        })

        if (!loginResponse.ok) {
          throw new Error('Invalid or expired reset link')
        }
      } else {
        // Otherwise, try to login with temp password first
        // Then change password
        const loginResponse = await fetch(`${config.apiBaseUrl}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, password: tempPassword }),
        })

        if (!loginResponse.ok) {
          throw new Error('Invalid temporary password')
        }
      }

      // Now change the password
      const changeResponse = await fetch(`${config.apiBaseUrl}/api/users/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: tempPassword,
          new_password: newPassword,
        }),
      })

      if (!changeResponse.ok) {
        const errorData = await changeResponse.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || 'Failed to change password')
      }

      // Success - redirect to login
      setIsSuccess(true)
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password changed successfully. Please login with your new password.' } })
      }, 1500)
    } catch (err: any) {
      const message = (err as Error)?.message || ''
      if (message.includes('ECONNREFUSED') || message.includes('Failed to fetch')) {
        setErrors({ general: 'Unable to connect to server. Please ensure the backend is running on port 5000.' })
      } else if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('expired')) {
        setErrors({ tempPassword: message })
      } else {
        setErrors({ general: message || 'Failed to change password. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Show email form (forgot password flow)
  if (!isTempPasswordFlow) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)" }}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="mb-6 text-center">
              <div className="flex justify-center items-center gap-2">
                <a href="https://gotandem.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90">
                  <img className="h-12 w-auto" src="/images/tandem-logo.svg" alt="Tandem" />
                  <span className="text-2xl font-bold text-gray-900">Tandem</span>
                </a>
              </div>
              <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Forgot your password?</h2>
              <p className="mt-1 text-sm text-gray-600">Enter your email and we'll send you a reset link.</p>
            </div>

            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {errors.general}
                </p>
              </div>
            )}

            {isSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">Reset email sent! Redirecting...</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmitEmail}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`input pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (errors.email) setErrors(prev => { const newErrors = { ...prev }; delete newErrors.email; return newErrors })
                    }}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <button type="submit" disabled={isLoading || isSuccess} className="w-full btn-primary flex justify-center items-center disabled:opacity-50">
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-tandem-blue hover:text-blue-600">Back to sign in</Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show password change form (temp password flow)
  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)" }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6 text-center">
            <div className="flex justify-center items-center gap-2">
              <a href="https://gotandem.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90">
                <img className="h-12 w-auto" src="/images/tandem-logo.svg" alt="Tandem" />
                <span className="text-2xl font-bold text-gray-900">Tandem</span>
              </a>
            </div>
            <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Change your password</h2>
            <p className="mt-1 text-sm text-gray-600">Enter your temporary password and choose a new password.</p>
          </div>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {errors.general}
              </p>
            </div>
          )}

          {isSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">Password changed successfully! Redirecting...</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmitPasswordChange}>
            {!oneTimeToken && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="input pl-10 bg-gray-50 cursor-not-allowed"
                    placeholder="you@company.com"
                    value={email}
                    readOnly
                    disabled
                  />
                </div>
                {user?.email && (
                  <p className="mt-1 text-xs text-gray-500">Email is prepopulated from your account</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="tempPassword" className="block text-sm font-medium text-gray-700">Temporary Password</label>
              <p className="mt-1 text-xs text-gray-500">Enter the temporary password from your email</p>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="tempPassword"
                  name="tempPassword"
                  type={showTempPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`input pl-10 pr-10 ${errors.tempPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter temporary password"
                  value={tempPassword}
                  onChange={(e) => {
                    setTempPassword(e.target.value)
                    if (errors.tempPassword) setErrors(prev => { const newErrors = { ...prev }; delete newErrors.tempPassword; return newErrors })
                  }}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowTempPassword(!showTempPassword)}
                >
                  <span className="text-sm text-gray-500 hover:text-gray-700">{showTempPassword ? 'Hide' : 'Show'}</span>
                </button>
              </div>
              {errors.tempPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.tempPassword}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <p className="mt-1 text-xs text-gray-500">At least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character</p>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input pl-10 pr-10 ${errors.newPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    if (errors.newPassword) setErrors(prev => { const newErrors = { ...prev }; delete newErrors.newPassword; return newErrors })
                    if (errors.confirmPassword && e.target.value === confirmPassword) {
                      setErrors(prev => { const newErrors = { ...prev }; delete newErrors.confirmPassword; return newErrors })
                    }
                  }}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <span className="text-sm text-gray-500 hover:text-gray-700">{showNewPassword ? 'Hide' : 'Show'}</span>
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.newPassword}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirmPassword) {
                      if (e.target.value === newPassword) {
                        setErrors(prev => { const newErrors = { ...prev }; delete newErrors.confirmPassword; return newErrors })
                      } else {
                        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }))
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <span className="text-sm text-gray-500 hover:text-gray-700">{showConfirmPassword ? 'Hide' : 'Show'}</span>
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Link
                to="/login"
                className="flex-1 btn-secondary text-center"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isLoading || isSuccess}
                className="flex-1 btn-primary flex justify-center items-center disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Changing...' : 'Confirm'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-tandem-blue hover:text-blue-600">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
